import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProjectsStore } from '../store/projects';
import { useFlightsStore } from '../store/flights';
import { useAuthStore, MetaOverviewPreferences } from '../store/auth';
import { api } from '../services/api';
import { Post, PostType } from '../store/posts';
import { PostDetailModal } from '../components/PostDetailModal';
import { usePostsStore } from '../store/posts';
import { POST_STATUS, PostStatus, PLATFORM_STATS } from '../config/statusConfig';
import { StatsCard, StatsCardGrid } from '../components/StatsCard';
import { PageHeader } from '../components/PageHeader';
import { ExternalPostsTab } from '../components/ExternalPostsTab';

type ViewTab = 'our-posts' | 'platform-posts';

// MetaIcon - matches header navigation (globe icon)
function MetaIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

interface PublishedPostsResponse {
  success: boolean;
  data: Post[];
  stats: {
    total: number;
    instagram: number;
    facebook: number;
  };
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

type GroupBy = 'platform' | 'date' | 'postType' | 'flight' | 'status' | 'none';
type TimeRange = 'all' | '7d' | '30d' | '90d' | '365d';

export function MetaOverview() {
  const { t } = useTranslation(['analytics', 'common', 'posts']);
  const [searchParams, setSearchParams] = useSearchParams();
  const { projects, fetchProjects } = useProjectsStore();
  const { flights, fetchFlights } = useFlightsStore();
  const { user, updatePreferences } = useAuthStore();
  const { updatePost, deletePost, cancelPost, duplicatePost } = usePostsStore();

  // Get saved preferences from user
  const savedPrefs = user?.preferences?.metaOverview;

  // State from preferences or defaults
  const [selectedProjectId, setSelectedProjectId] = useState<string>(savedPrefs?.selectedProjectId || '');
  const [posts, setPosts] = useState<Post[]>([]);
  const [stats, setStats] = useState({ total: 0, instagram: 0, facebook: 0 });
  const [isLoading, setIsLoading] = useState(true);

  // Filters - initialize from saved preferences
  const [platformFilter, setPlatformFilter] = useState<string>(savedPrefs?.platformFilter || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [postTypeFilter, setPostTypeFilter] = useState<PostType | 'all'>(savedPrefs?.postTypeFilter || 'all');
  const [timeRange, setTimeRange] = useState<TimeRange>(savedPrefs?.timeRange || 'all');
  const [flightFilter, setFlightFilter] = useState<string>(savedPrefs?.flightFilter || 'all');

  // Grouping - initialize from saved preferences
  const [groupBy, setGroupBy] = useState<GroupBy>(savedPrefs?.groupBy || 'platform');

  // Post Detail Modal
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Tab state - our posts vs platform posts
  const [activeTab, setActiveTab] = useState<ViewTab>('our-posts');

  // Save preferences to DB (debounced)
  const savePreferences = useCallback(async (prefs: Partial<MetaOverviewPreferences>) => {
    const currentMetaPrefs = user?.preferences?.metaOverview || {};
    const newMetaPrefs = { ...currentMetaPrefs, ...prefs };
    await updatePreferences({ metaOverview: newMetaPrefs as MetaOverviewPreferences });
  }, [user?.preferences?.metaOverview, updatePreferences]);

  // Check for project param in URL (for cross-module navigation)
  useEffect(() => {
    const projectParam = searchParams.get('project');
    if (projectParam && projects.length > 0) {
      const projectExists = projects.some(p => p.id === projectParam);
      if (projectExists && projectParam !== selectedProjectId) {
        setSelectedProjectId(projectParam);
        // Also save to preferences
        savePreferences({ selectedProjectId: projectParam });
      }
    }
  }, [searchParams, projects, selectedProjectId, savePreferences]);

  // Check for post param in URL (for opening modal)
  useEffect(() => {
    const postId = searchParams.get('post');
    if (postId && posts.length > 0) {
      const post = posts.find(p => p.id === postId);
      if (post) {
        setSelectedPost(post);
        setIsModalOpen(true);
      }
    }
  }, [searchParams, posts]);

  // Load projects and flights on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchFlights(selectedProjectId);
    }
  }, [selectedProjectId, fetchFlights]);

  // Load published posts
  useEffect(() => {
    async function loadPosts() {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedProjectId) {
          params.append('project_id', selectedProjectId);
        }
        if (platformFilter !== 'all') {
          params.append('platform', platformFilter);
        }
        const response = await api.get<PublishedPostsResponse>(`/posts/published?${params}`);
        setPosts(response.data);
        setStats(response.stats);
      } catch (error) {
        console.error('Failed to load published posts:', error);
      } finally {
        setIsLoading(false);
      }
    }
    loadPosts();
  }, [selectedProjectId, platformFilter]);

  // Filter posts
  const filteredPosts = useMemo(() => {
    let result = posts;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (post) =>
          post.text?.toLowerCase().includes(query) ||
          post.display_id?.toLowerCase().includes(query) ||
          post.hashtags?.some(h => h.toLowerCase().includes(query))
      );
    }

    // Post type filter
    if (postTypeFilter !== 'all') {
      result = result.filter((post) => post.post_type === postTypeFilter);
    }

    // Time range filter
    if (timeRange !== 'all') {
      const now = new Date();
      const days = parseInt(timeRange);
      const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      result = result.filter((post) => {
        const publishedAt = post.published_at ? new Date(post.published_at) : null;
        return publishedAt && publishedAt >= cutoff;
      });
    }

    // Flight filter
    if (flightFilter !== 'all') {
      if (flightFilter === 'none') {
        result = result.filter((post) => !post.flight_id);
      } else {
        result = result.filter((post) => post.flight_id === flightFilter);
      }
    }

    return result;
  }, [posts, searchQuery, postTypeFilter, timeRange, flightFilter]);

  // Helper function to get group labels
  const getGroupLabel = useCallback((groupType: string, key: string): string => {
    const groupLabels: Record<string, Record<string, string>> = {
      date: {
        today: t('analytics:meta.groupLabels.today'),
        yesterday: t('analytics:meta.groupLabels.yesterday'),
        week: t('analytics:meta.groupLabels.thisWeek'),
        month: t('analytics:meta.groupLabels.thisMonth'),
        quarter: t('analytics:meta.groupLabels.lastQuarter'),
        older: t('analytics:meta.groupLabels.older'),
        unknown: t('analytics:meta.groupLabels.unknown'),
      },
      postType: {
        feed: t('analytics:meta.groupLabels.feedPosts'),
        reel: t('analytics:meta.groupLabels.reels'),
        story: t('analytics:meta.groupLabels.stories'),
        carousel: t('analytics:meta.groupLabels.carousels'),
      },
    };
    return groupLabels[groupType]?.[key] || key;
  }, [t]);

  // Group posts based on selected grouping
  const groupedPosts = useMemo(() => {
    const groups: Record<string, { label: string; icon?: string; color?: string; posts: Post[] }> = {};

    if (groupBy === 'none') {
      return { all: { label: t('analytics:meta.groupLabels.allPosts'), posts: filteredPosts } };
    }

    filteredPosts.forEach((post) => {
      let groupKey: string;
      let groupLabel: string;
      let groupIcon: string | undefined;
      let groupColor: string | undefined;

      switch (groupBy) {
        case 'platform':
          // Group by each platform the post was published to
          post.platforms.forEach((platform) => {
            groupKey = platform;
            groupLabel = platform === 'instagram' ? t('analytics:meta.instagram') : t('analytics:meta.facebook');
            groupIcon = platform;
            groupColor = platform === 'instagram' ? 'pink' : 'blue';

            if (!groups[groupKey]) {
              groups[groupKey] = { label: groupLabel, icon: groupIcon, color: groupColor, posts: [] };
            }
            groups[groupKey].posts.push(post);
          });
          return; // Skip the default group assignment

        case 'date':
          const publishedAt = post.published_at ? new Date(post.published_at) : null;
          if (!publishedAt) {
            groupKey = 'unknown';
            groupLabel = getGroupLabel('date', 'unknown');
          } else {
            const now = new Date();
            const diffDays = Math.floor((now.getTime() - publishedAt.getTime()) / (1000 * 60 * 60 * 24));

            if (diffDays === 0) {
              groupKey = 'today';
              groupLabel = getGroupLabel('date', 'today');
            } else if (diffDays === 1) {
              groupKey = 'yesterday';
              groupLabel = getGroupLabel('date', 'yesterday');
            } else if (diffDays < 7) {
              groupKey = 'week';
              groupLabel = getGroupLabel('date', 'week');
            } else if (diffDays < 30) {
              groupKey = 'month';
              groupLabel = getGroupLabel('date', 'month');
            } else if (diffDays < 90) {
              groupKey = 'quarter';
              groupLabel = getGroupLabel('date', 'quarter');
            } else {
              groupKey = 'older';
              groupLabel = getGroupLabel('date', 'older');
            }
          }
          break;

        case 'postType':
          groupKey = post.post_type || 'feed';
          groupLabel = getGroupLabel('postType', groupKey);
          break;

        case 'flight':
          if (post.flight_id) {
            const flight = flights.find(f => f.id === post.flight_id);
            groupKey = post.flight_id;
            groupLabel = flight?.name || t('analytics:meta.groupLabels.unknownFlight');
            groupColor = flight?.color;
          } else {
            groupKey = 'no-flight';
            groupLabel = t('analytics:meta.groupLabels.withoutFlight');
          }
          break;

        case 'status':
          groupKey = post.status;
          groupLabel = t(`posts:status.${post.status}`, { defaultValue: post.status });
          break;

        default:
          groupKey = 'all';
          groupLabel = t('analytics:meta.groupLabels.allPosts');
      }

      if (!groups[groupKey]) {
        groups[groupKey] = { label: groupLabel, icon: groupIcon, color: groupColor, posts: [] };
      }
      groups[groupKey].posts.push(post);
    });

    // Sort groups
    const sortedGroups: typeof groups = {};
    const sortOrder: Record<GroupBy, string[]> = {
      platform: ['instagram', 'facebook'],
      date: ['today', 'yesterday', 'week', 'month', 'quarter', 'older', 'unknown'],
      postType: ['feed', 'carousel', 'reel', 'story'],
      flight: [], // Will be sorted by post count
      status: ['published', 'scheduled', 'publishing', 'draft', 'failed', 'cancelled'],
      none: ['all'],
    };

    if (groupBy === 'flight') {
      // Sort flights by post count (descending)
      Object.entries(groups)
        .sort((a, b) => b[1].posts.length - a[1].posts.length)
        .forEach(([key, value]) => {
          sortedGroups[key] = value;
        });
    } else {
      const order = sortOrder[groupBy];
      order.forEach((key) => {
        if (groups[key]) {
          sortedGroups[key] = groups[key];
        }
      });
      // Add any remaining groups not in the order
      Object.keys(groups).forEach((key) => {
        if (!sortedGroups[key]) {
          sortedGroups[key] = groups[key];
        }
      });
    }

    return sortedGroups;
  }, [filteredPosts, groupBy, flights, t, getGroupLabel]);

  // Format relative time
  const formatRelativeTime = useCallback((dateStr: string | null) => {
    if (!dateStr) return t('analytics:meta.relativeTime.unknown');
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('analytics:meta.relativeTime.today');
    if (diffDays === 1) return t('analytics:meta.relativeTime.yesterday');
    if (diffDays < 7) return t('analytics:meta.relativeTime.daysAgo', { days: diffDays });
    if (diffDays < 30) return t('analytics:meta.relativeTime.weeksAgo', { weeks: Math.floor(diffDays / 7) });
    return t('analytics:meta.relativeTime.monthsAgo', { months: Math.floor(diffDays / 30) });
  }, [t]);

  // Handle post click - open modal on this page instead of navigating away
  const handlePostClick = (post: Post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
    // Update URL so the state is shareable (keep project param)
    const newParams = new URLSearchParams(searchParams);
    newParams.set('post', post.id);
    setSearchParams(newParams);
  };

  // Handle modal close - stay on Meta page
  const handleModalClose = () => {
    setSelectedPost(null);
    setIsModalOpen(false);
    // Clear post param from URL but keep project
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('post');
    setSearchParams(newParams);
  };

  // Handle post update
  const handlePostUpdate = async (postId: string, data: Parameters<typeof updatePost>[1]) => {
    await updatePost(postId, data);
    // Refresh posts list
    const params = new URLSearchParams();
    if (selectedProjectId) params.append('project_id', selectedProjectId);
    if (platformFilter !== 'all') params.append('platform', platformFilter);
    const response = await api.get<PublishedPostsResponse>(`/posts/published?${params}`);
    setPosts(response.data);
    setStats(response.stats);
    // Update selected post if still viewing
    const updatedPost = response.data.find(p => p.id === postId);
    if (updatedPost) setSelectedPost(updatedPost);
  };

  // Handle post delete
  const handlePostDelete = async (postId: string, options?: { deleteFromPlatforms?: boolean }) => {
    const result = await deletePost(postId, options);
    if (result.success) {
      // Refresh posts list
      const params = new URLSearchParams();
      if (selectedProjectId) params.append('project_id', selectedProjectId);
      if (platformFilter !== 'all') params.append('platform', platformFilter);
      const response = await api.get<PublishedPostsResponse>(`/posts/published?${params}`);
      setPosts(response.data);
      setStats(response.stats);
    }
    return result;
  };

  // Handle post cancel
  const handlePostCancel = async (postId: string) => {
    await cancelPost(postId);
    // Refresh posts list
    const params = new URLSearchParams();
    if (selectedProjectId) params.append('project_id', selectedProjectId);
    if (platformFilter !== 'all') params.append('platform', platformFilter);
    const response = await api.get<PublishedPostsResponse>(`/posts/published?${params}`);
    setPosts(response.data);
    setStats(response.stats);
  };

  // Wrapper functions that save preferences when filters change
  const handleProjectChange = (value: string) => {
    setSelectedProjectId(value);
    setFlightFilter('all'); // Reset flight filter when project changes
    savePreferences({ selectedProjectId: value, flightFilter: 'all' });
    // Update URL to share project selection
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('project', value);
    } else {
      newParams.delete('project');
    }
    // Keep post param if modal is open
    if (!isModalOpen) {
      newParams.delete('post');
    }
    setSearchParams(newParams);
  };

  const handlePlatformFilterChange = (value: string) => {
    setPlatformFilter(value);
    savePreferences({ platformFilter: value as MetaOverviewPreferences['platformFilter'] });
  };

  const handleTimeRangeChange = (value: TimeRange) => {
    setTimeRange(value);
    savePreferences({ timeRange: value });
  };

  const handlePostTypeFilterChange = (value: PostType | 'all') => {
    setPostTypeFilter(value);
    savePreferences({ postTypeFilter: value });
  };

  const handleFlightFilterChange = (value: string) => {
    setFlightFilter(value);
    savePreferences({ flightFilter: value });
  };

  const handleGroupByChange = (value: GroupBy) => {
    setGroupBy(value);
    savePreferences({ groupBy: value });
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || postTypeFilter !== 'all' || timeRange !== 'all' || flightFilter !== 'all';

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery('');
    setPostTypeFilter('all');
    setTimeRange('all');
    setFlightFilter('all');
    // Save reset state to preferences
    savePreferences({
      postTypeFilter: 'all',
      timeRange: 'all',
      flightFilter: 'all',
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        icon={<MetaIcon className="w-7 h-7" />}
        title={t('analytics:meta.title')}
        subtitle={t('analytics:meta.subtitle')}
        actions={
          <select
            value={selectedProjectId}
            onChange={(e) => handleProjectChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('analytics:meta.allProjects')}</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        }
      />

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 p-1 bg-gray-100 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('our-posts')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'our-posts'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('analytics:meta.ourPosts')}
        </button>
        <button
          onClick={() => setActiveTab('platform-posts')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'platform-posts'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('analytics:meta.platformPosts')}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'platform-posts' ? (
        <ExternalPostsTab />
      ) : (
        <>
        {/* Stats Cards */}
      <div className="mb-6">
        <StatsCardGrid columns={3}>
          <StatsCard
            value={stats.total}
            label={t('posts:status.published')}
            config={POST_STATUS.published}
            isActive={platformFilter === 'all'}
            onClick={() => handlePlatformFilterChange('all')}
          />
          <StatsCard
            value={stats.instagram}
            label={PLATFORM_STATS.instagram.label}
            config={PLATFORM_STATS.instagram}
            isActive={platformFilter === 'instagram'}
            onClick={() => handlePlatformFilterChange('instagram')}
            icon={<InstagramIcon className="w-5 h-5 text-pink-600" />}
          />
          <StatsCard
            value={stats.facebook}
            label={PLATFORM_STATS.facebook.label}
            config={PLATFORM_STATS.facebook}
            isActive={platformFilter === 'facebook'}
            onClick={() => handlePlatformFilterChange('facebook')}
            icon={<FacebookIcon className="w-5 h-5 text-blue-600" />}
          />
        </StatsCardGrid>
      </div>

      {/* Filters & Grouping */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('analytics:meta.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Time Range Filter */}
          <select
            value={timeRange}
            onChange={(e) => handleTimeRangeChange(e.target.value as TimeRange)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('analytics:meta.allPeriods')}</option>
            <option value="7d">{t('analytics:meta.last7Days')}</option>
            <option value="30d">{t('analytics:meta.last30Days')}</option>
            <option value="90d">{t('analytics:meta.last90Days')}</option>
            <option value="365d">{t('analytics:meta.lastYear')}</option>
          </select>

          {/* Post Type Filter */}
          <select
            value={postTypeFilter}
            onChange={(e) => handlePostTypeFilterChange(e.target.value as PostType | 'all')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('analytics:meta.allTypes')}</option>
            <option value="feed">{t('analytics:meta.feed')}</option>
            <option value="carousel">{t('analytics:meta.carousel')}</option>
            <option value="reel">{t('analytics:meta.reel')}</option>
            <option value="story">{t('analytics:meta.story')}</option>
          </select>

          {/* Flight Filter (only if project selected) */}
          {selectedProjectId && flights.length > 0 && (
            <select
              value={flightFilter}
              onChange={(e) => handleFlightFilterChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('analytics:meta.allFlights')}</option>
              <option value="none">{t('analytics:meta.withoutFlight')}</option>
              {flights.map((flight) => (
                <option key={flight.id} value={flight.id}>
                  {flight.name}
                </option>
              ))}
            </select>
          )}

          {/* Grouping Selector */}
          <div className="flex items-center gap-2 border-l border-gray-200 pl-3">
            <span className="text-xs text-gray-500">{t('analytics:meta.groupBy')}</span>
            <select
              value={groupBy}
              onChange={(e) => handleGroupByChange(e.target.value as GroupBy)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="platform">{t('analytics:meta.byPlatform')}</option>
              <option value="status">{t('analytics:meta.byStatus')}</option>
              <option value="date">{t('analytics:meta.byDate')}</option>
              <option value="postType">{t('analytics:meta.byType')}</option>
              {selectedProjectId && <option value="flight">{t('analytics:meta.byFlight')}</option>}
              <option value="none">{t('analytics:meta.noGrouping')}</option>
            </select>
          </div>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="text-sm text-blue-600 hover:text-blue-700 whitespace-nowrap"
            >
              {t('analytics:meta.resetFilters')}
            </button>
          )}
        </div>

        {/* Active Filter Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                {t('analytics:meta.searchLabel')} "{searchQuery}"
                <button onClick={() => setSearchQuery('')} className="hover:text-gray-900">×</button>
              </span>
            )}
            {postTypeFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                {t('analytics:meta.typeLabel')} {postTypeFilter}
                <button onClick={() => setPostTypeFilter('all')} className="hover:text-purple-900">×</button>
              </span>
            )}
            {timeRange !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                {t('analytics:meta.periodLabel')} {timeRange === '7d' ? t('analytics:meta.last7Days') : timeRange === '30d' ? t('analytics:meta.last30Days') : timeRange === '90d' ? t('analytics:meta.last90Days') : t('analytics:meta.lastYear')}
                <button onClick={() => setTimeRange('all')} className="hover:text-green-900">×</button>
              </span>
            )}
            {flightFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                {t('analytics:meta.flightLabel')} {flightFilter === 'none' ? t('analytics:meta.withoutFlight') : flights.find(f => f.id === flightFilter)?.name}
                <button onClick={() => setFlightFilter('all')} className="hover:text-orange-900">×</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {t('analytics:meta.postsFound', { count: filteredPosts.length })}
          {hasActiveFilters && ` ${t('analytics:meta.ofTotal', { total: posts.length })}`}
        </p>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredPosts.length === 0 ? (
        /* Empty State */
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <CheckCircleIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {t('analytics:meta.noPublishedPosts')}
          </h3>
          <p className="text-gray-500">
            {hasActiveFilters
              ? t('analytics:meta.noPostsFound')
              : t('analytics:meta.publishToSee')}
          </p>
        </div>
      ) : (
        /* Grouped Posts */
        <div className="space-y-8">
          {Object.entries(groupedPosts).map(([groupKey, group]) => {
            // Get status config for status grouping
            const statusConfig = groupBy === 'status' ? POST_STATUS[groupKey as PostStatus] : null;

            return (
            <section key={groupKey}>
              {/* Group Header */}
              <div className="flex items-center gap-2 mb-4">
                {group.icon === 'instagram' && <InstagramIcon className="w-5 h-5 text-pink-600" />}
                {group.icon === 'facebook' && <FacebookIcon className="w-5 h-5 text-blue-600" />}
                {statusConfig && (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
                    <span className={`w-2 h-2 rounded-full ${statusConfig.dot}`} />
                    {t(`posts:status.${groupKey}`)}
                  </span>
                )}
                {group.color && !group.icon && !statusConfig && (
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: group.color }}
                  />
                )}
                {!statusConfig && (
                  <h2 className="text-lg font-semibold text-gray-900">
                    {group.label}
                  </h2>
                )}
                <span className="text-sm font-normal text-gray-500">
                  ({group.posts.length})
                </span>
              </div>

              {/* Posts Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {group.posts.map((post) => (
                  <PostCard
                    key={`${groupKey}-${post.id}`}
                    post={post}
                    onClick={() => handlePostClick(post)}
                    formatRelativeTime={formatRelativeTime}
                    showPlatformBadges={groupBy !== 'platform'}
                    t={t}
                  />
                ))}
              </div>
            </section>
          );
          })}
        </div>
      )}

      {/* Post Detail Modal */}
      <PostDetailModal
        post={selectedPost}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onUpdate={handlePostUpdate}
        onDelete={handlePostDelete}
        onCancel={handlePostCancel}
        onDuplicate={async (postId) => {
          await duplicatePost(postId);
          handleModalClose();
          // Refresh posts
          if (selectedProjectId) {
            const response = await fetch(`/api/posts/published?project_id=${selectedProjectId}`, {
              credentials: 'include',
            });
            if (response.ok) {
              const data = await response.json();
              setPosts(data.data || []);
            }
          }
        }}
        flights={flights.map(f => ({ id: f.id, name: f.name, color: f.color || '#6B7280' }))}
      />
        </>
      )}
    </div>
  );
}

// Post Card Component
interface PostCardProps {
  post: Post;
  onClick: () => void;
  formatRelativeTime: (date: string | null) => string;
  showPlatformBadges?: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function PostCard({ post, onClick, formatRelativeTime, showPlatformBadges = true, t }: PostCardProps) {
  const thumbnailUrl = post.media_urls?.[0];

  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md hover:border-gray-300 transition-all"
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-gray-100 relative">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-gray-300" />
          </div>
        )}

        {/* Platform Badges */}
        {showPlatformBadges && (
          <div className="absolute top-2 left-2 flex gap-1">
            {post.platforms.includes('instagram') && (
              <div className="px-1.5 py-0.5 rounded text-xs font-medium bg-pink-100 text-pink-700">
                IG
              </div>
            )}
            {post.platforms.includes('facebook') && (
              <div className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                FB
              </div>
            )}
          </div>
        )}

        {/* Post Type Badge */}
        {post.post_type && post.post_type !== 'feed' && (
          <div className="absolute bottom-2 left-2 px-1.5 py-0.5 bg-black/60 text-white rounded text-xs font-medium">
            {post.post_type === 'reel' ? `▶ ${t('analytics:meta.postTypes.reel')}` : post.post_type === 'carousel' ? `◫ ${t('analytics:meta.postTypes.carousel')}` : `◯ ${t('analytics:meta.postTypes.story')}`}
          </div>
        )}

        {/* Multiple Media Indicator */}
        {post.media_urls && post.media_urls.length > 1 && (
          <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-black/60 text-white rounded text-xs font-medium">
            +{post.media_urls.length - 1}
          </div>
        )}

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <ExternalLinkIcon className="w-6 h-6 text-white drop-shadow-lg" />
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-2">
        {/* Text Preview */}
        <p className="text-xs text-gray-700 line-clamp-2 min-h-[2rem]">
          {post.text || <span className="text-gray-400 italic">{t('analytics:meta.noText')}</span>}
        </p>

        {/* Meta Info */}
        <div className="mt-2 flex items-center justify-between text-[10px] text-gray-400">
          <span>{formatRelativeTime(post.published_at)}</span>
          <span className="font-mono">{post.display_id}</span>
        </div>
      </div>
    </div>
  );
}

// Icons
function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ImageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

export default MetaOverview;
